import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  propagateQuerytoExternalLinks,
  urlIsExternal,
  applyGanalyticsTracking,
  trackButtonActivity,
} from 'src/functions/propagateQueryParams';
import { windowIsLoaded } from 'src/functions/windowIsLoaded';

import { palette } from 'src/styles/colors';
import getButtonStyleByColorScheme, { ColorScheme } from 'src/styles/functions/getButtonStyleByColorScheme';
import { StyledLinkIconSpan, StyledLinkSimple, StyledLinkSimpleProps } from './Link.simple.styles';
import { StandardParagraphSize } from '../typography/paragraph.styles';

export interface LinkSimpleProps extends StyledLinkSimpleProps {
  text: string;
  id?: string;
  underlined?: boolean;
  color?: 'main' | 'text' | 'violet';
  colorScheme?: ColorScheme;
  url?: string;
  smooth?: boolean;
  icon?: React.ReactNode;
  iconPosition?: 'left' | 'right';
  justify?: 'stretch' | 'center' | 'left' | 'right';
}

const LinkSimple = ({
  text,
  url,
  id,
  justify,
  color,
  underlined,
  icon,
  smooth,
  size,
  sizeXlg,
  sizeLg,
  sizeSubLg,
  sizeMd,
  sizeSm,
  sizeXs,
  sizeXxs,
  iconPosition,
  colorScheme = 'Standard',
  standardSize,
}: LinkSimpleProps): JSX.Element => {
  const buttonRef = useRef<HTMLAnchorElement>();
  const mountedComponentRef = useRef(true);
  const [queryPropagatedUrl, updateHref] = useState(url);

  useEffect(() => {
    windowIsLoaded().then(() => {
      if (!mountedComponentRef.current) return null;

      updateHref(propagateQuerytoExternalLinks(url));
    });
  }, [url]);

  useEffect(() => {
    return () => {
      mountedComponentRef.current = false;
    };
  }, []);

  const onClickToScroll = (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
    event.preventDefault();
    document.querySelector(url).scrollIntoView({
      behavior: 'smooth',
    });
  };

  // Smooth scroll only works when url is ID type
  const navigationProp = smooth && url.includes('#') ? {} : { href: queryPropagatedUrl || '#' };
  const targetProp = useMemo(() => {
    return urlIsExternal(url) ? { target: '_blank', rel: 'noreferrer' } : {};
  }, [url]);
  const linkColors = useMemo(() => getButtonStyleByColorScheme(colorScheme, 'tertiary'), []);

  // Extend onClick listener for Trackers, etc.
  const trackOnClick = (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
    const baseOnClick =
      smooth && url.includes('#') ? onClickToScroll : applyGanalyticsTracking(queryPropagatedUrl, url);
    baseOnClick(e);
    // >> Additional trackers
    trackButtonActivity(buttonRef.current, text);
  };

  const standardSizeDesktop = StandardParagraphSize[standardSize]?.desktop;
  const standardSizeLg = StandardParagraphSize[standardSize]?.lg;
  const standardSizeSm = StandardParagraphSize[standardSize]?.sm;

  return (
    <StyledLinkSimple
      className='styled-link-simple'
      justify={justify}
      textColor={color === 'text' ? palette.text : linkColors.textColor}
      hasIcon={!!icon}
      iconPosition={iconPosition}
      underlined={underlined}
      size={size ?? standardSizeDesktop}
      sizeXlg={sizeXlg}
      sizeLg={sizeLg ?? standardSizeLg}
      sizeSubLg={sizeSubLg}
      sizeMd={sizeMd}
      sizeSm={standardSizeSm ?? sizeSm}
      sizeXs={sizeXs}
      sizeXxs={sizeXxs}
      id={id}
      ref={buttonRef}
      onClick={trackOnClick}
      {...navigationProp}
      {...targetProp}
    >
      {icon ? (
        <StyledLinkIconSpan
          iconPosition={iconPosition}
          color={color}
        >
          {' '}
          {icon}
        </StyledLinkIconSpan>
      ) : null}

      <span>{text}</span>
    </StyledLinkSimple>
  );
};

export default LinkSimple;
